import AddMembersToPlatformDialog from '@/components/AddMembersToPlatformDialog/AddMembersToPlatformDialog';
import { AnyUser } from '@/components/ProjectSettingWithUserRoleTable/types';
import SearchBox from '@/components/Text/SearchBox';
import { useExternalSearchText } from '@/hooks/MaterialTable';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import {
  useGetOrgAdminProjectDetailsQuery,
  useOrgAdminUpdateProjectMembersMutation,
} from '@/serverStore/projects';
import { inviteUser } from '@/store/user_store';
import { User, UserId, UserPermission } from '@clef/shared/types';
import {
  Box,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  makeStyles,
  Popover,
  Typography,
} from '@material-ui/core';
import { Button, SwitchButtonGroup } from '@clef/client-library';
import { useSnackbar } from 'notistack';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import OrgAdminProjectSettingsUserTable, {
  UserPermissions,
} from '@/components/OrgAdminProjectSettingsUserTable/OrgAdminProjectSettingsUserTable';
import { DialogPage } from '.';
import LockOutlined from '@material-ui/icons/LockOutlined';
import HomeWorkOutlined from '@material-ui/icons/HomeWorkOutlined';
import { projectSettingWithUserRoleStyles } from '@/components/ProjectSettingWithUserRole/projectSettingWithUserRoleStyles';
import CheckOutlined from '@material-ui/icons/CheckOutlined';
import classNames from 'classnames';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { SwitchButton } from '@clef/client-library/src/components/SwitchButtonGroup';
import { useGetGroupsQuery } from '@/serverStore/account/queries';
import { emptyGroups } from '@clef/shared/utils';

const projectAccessDialogStyles = makeStyles(theme => ({
  dialogBox: {
    paddingBottom: theme.spacing(2),
  },
  dialogActions: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  accessRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  inviteMembersBtn: {
    alignSelf: 'left',
  },
  searchBar: {
    marginBottom: theme.spacing(2),
    minHeight: '60px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    gap: theme.spacing(4),
  },
  searchBoxContainer: {
    display: 'relative',
    flexGrow: 1,
  },
}));

type ManageProjectAccessDialogProps = {
  open: boolean;
  defaultTab?: 'manage-access' | 'invite-members';
  onCancel: () => void;
  openConfirmationDialog?: (confirmationDialog: DialogPage) => void;
  onUserRemovalStart?: (user: User) => void;
  onTransferOwnershipStart?: (user: User) => void;
  onVisibilityChangeStart: (newAccessMode: AccessMode) => void;
  projectId: number;
};

enum AccessMode {
  Private,
  Public,
}

export const ManageProjectAccessDialog: React.FC<ManageProjectAccessDialogProps> = ({
  open,
  defaultTab = 'manage-access',
  onCancel,
  projectId,
  onUserRemovalStart,
  onTransferOwnershipStart,
  onVisibilityChangeStart,
}) => {
  const allOrgUsers = useTypedSelector(state => state.user.users);
  const { data: selectedProject, isLoading: isQuerySelectedProjectLoading } =
    useGetOrgAdminProjectDetailsQuery(projectId ?? 0);
  const selectedProjectUsers = selectedProject?.users;
  const styles = projectAccessDialogStyles();
  const accessStyles = projectSettingWithUserRoleStyles();
  const accessBoxRef = useRef<HTMLDivElement | null>(null);

  const [openInvite, setOpenInvite] = useState<boolean>(false);
  const [selectedUsers, setSelectedUsers] = useState<AnyUser[]>([]);
  const [userIdsInProject, setUserIdsInProject] = useState<AnyUser['id'][]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [mode, setMode] = useState<'manage-access' | 'invite-members'>(defaultTab);
  const [userPermissions, setUserPermissions] = useState<UserPermissions>({} as UserPermissions);

  useEffect(() => {
    setMode(defaultTab);
  }, [defaultTab, open]);

  const loginUser = useTypedSelector(state => state.login.user!);
  const { data: groups = emptyGroups } = useGetGroupsQuery({ isEnabled: loginUser.orgEnableGroup });

  const [editAccess, setEditAccess] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const tableRef = useRef<any>();
  const { setSearchText } = useExternalSearchText(tableRef);

  const updateProjectMembers = useOrgAdminUpdateProjectMembersMutation();

  const _addMembers = async (): Promise<void> => {
    if (!selectedProject) return;
    setIsAdding(true);

    try {
      await updateProjectMembers.mutateAsync({
        orgId: selectedProject.orgId,
        projectId: selectedProject.id,
        projectName: selectedProject.name,
        numberOfUsersToAdd: selectedUsers.length,
        userPermissions: selectedUsers.map((user: AnyUser) => {
          return {
            id: user.id,
            permissions: Object.entries(userPermissions[user.id] ?? ({} as UserPermissions))
              .filter(([, value]) => {
                return value;
              })
              .map(([key]) => key) as UserPermission[],
          };
        }),
      });
    } catch (e) {
      enqueueSnackbar(t('An error occured while adding users to the project.'), {
        variant: 'error',
      });
    } finally {
      setIsAdding(false);
      setIsUpdating(false);
    }
  };

  const _updateMembers = async (
    userPermissions: {
      id: UserId;
      permissions: UserPermission[];
    }[],
  ): Promise<void> => {
    if (!selectedProject) return;
    setIsUpdating(true);

    try {
      await updateProjectMembers.mutateAsync({
        orgId: selectedProject.orgId,
        projectId: selectedProject.id,
        projectName: selectedProject.name,
        numberOfUsersToAdd: 1,
        userPermissions,
      });
      return Promise.resolve();
    } catch (e) {
      enqueueSnackbar(t('An error occured while updating users on the project.'), {
        variant: 'error',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const onAccessChange = useCallback(
    (newAccessMode: AccessMode) => {
      setEditAccess(false);
      onVisibilityChangeStart(newAccessMode);
    },
    [onVisibilityChangeStart],
  );

  const onUpdateAccessStart = useCallback(() => {
    setEditAccess(true);
  }, []);

  useEffect(() => {
    setSelectedUsers([]);
    setUserPermissions({});
  }, [mode]);

  useEffect(() => {
    const userIds = selectedProject?.users?.map(user => user.id) ?? [];
    setUserIdsInProject(userIds);
  }, [selectedProject]);

  const projectUsers = useMemo(() => {
    if (mode === 'invite-members') {
      return [...allOrgUsers, ...groups].filter(user => !userIdsInProject.includes(user.id)) ?? [];
    }
    return selectedProjectUsers ?? [];
  }, [selectedProjectUsers, userIdsInProject, mode, allOrgUsers, groups]);

  const onPermissionChange = useCallback(userPermissions => {
    setUserPermissions(userPermissions);
  }, []);

  if (!selectedProject) return null;

  const privateProjectAccess = (
    <>
      <LockOutlined className={accessStyles.accessIcon} />
      <Typography display="inline" className={accessStyles.accessLabel}>
        {t('Only invited members can access the project')}
      </Typography>
    </>
  );

  const publicProjectAccess = (
    <>
      <HomeWorkOutlined className={accessStyles.accessIcon} />
      <Typography className={accessStyles.accessLabel}>
        {t('Anyone in the organization can access the project')}
      </Typography>
    </>
  );

  const accessText = (
    <>
      <div ref={accessBoxRef} className={accessStyles.accessTitle}>
        <Popover
          open={editAccess}
          anchorEl={accessBoxRef.current}
          onClose={() => {
            setEditAccess(false);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          className={accessStyles.accessPopover}
          disableEnforceFocus
        >
          <Card className={accessStyles.selectAccess}>
            <Box
              className={classNames(accessStyles.selectItem, {
                active: selectedProject?.inviteOnly,
              })}
              display="flex"
              onClick={() => onAccessChange(AccessMode.Private)}
              data-testid="switch-access-with-private"
            >
              {selectedProject?.inviteOnly ? (
                <CheckOutlined className={accessStyles.checkAccess} />
              ) : (
                <Box className={accessStyles.notCheck} />
              )}
              {privateProjectAccess}
            </Box>
            <Box
              className={classNames(accessStyles.selectItem, {
                active: !selectedProject?.inviteOnly,
              })}
              display="flex"
              onClick={() => onAccessChange(AccessMode.Public)}
              data-testid="switch-access-with-public"
            >
              {!selectedProject?.inviteOnly ? (
                <CheckOutlined className={accessStyles.checkAccess} />
              ) : (
                <Box className={accessStyles.notCheck} />
              )}
              {publicProjectAccess}
            </Box>
          </Card>
        </Popover>
      </div>
      <Box display="flex" flexDirection="row" alignItems="center">
        {selectedProject?.inviteOnly ? privateProjectAccess : publicProjectAccess}
        <Button
          color="primary"
          onClick={onUpdateAccessStart}
          id="project-setting-switch-access-edit"
        >
          <ExpandMore />
        </Button>
      </Box>
    </>
  );

  return (
    <>
      <Dialog
        className="cy-pr-manage-project-access-dialog"
        data-testid="manage-project-access-dialog"
        open={open && !openInvite}
        onClose={onCancel}
        fullWidth={true}
        maxWidth="lg"
      >
        <DialogTitle>{t('Manage Project Access')}</DialogTitle>
        {isQuerySelectedProjectLoading ? (
          <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <DialogContent>
              <Typography>
                <b>{t('Project Name')}:</b> {selectedProject.name}
              </Typography>
            </DialogContent>
            <DialogContent className={styles.accessRow}>
              <Typography>
                <b>{t('Access')}:</b>
              </Typography>
              {accessText}
            </DialogContent>
            <DialogContent>
              <SwitchButtonGroup>
                <SwitchButton
                  active={mode === 'manage-access'}
                  onClick={() => setMode('manage-access')}
                >
                  {t('Manage Access')}
                </SwitchButton>
                <SwitchButton
                  active={mode === 'invite-members'}
                  onClick={() => setMode('invite-members')}
                >
                  {t('Invite Members')}
                </SwitchButton>
              </SwitchButtonGroup>
            </DialogContent>
            {mode === 'invite-members' && (
              <DialogContent className={styles.searchBar}>
                <div className={styles.searchBoxContainer}>
                  <SearchBox
                    type="text"
                    showSearchIcon={false}
                    placeholder={t('Type to search to add to project')}
                    onInput={(e: React.FormEvent): void => {
                      setSearchText((e.target as HTMLInputElement).value);
                    }}
                    style={{ width: '100%', height: '44px' }}
                  />
                </div>
                <Button
                  onClick={_addMembers}
                  variant="outlined"
                  color="primary"
                  id="project-setting-invite-btn"
                  disabled={isAdding || !selectedUsers.length}
                  style={{ height: '44px' }}
                >
                  {isAdding ? t('Inviting...') : t('Invite')}
                </Button>
              </DialogContent>
            )}
            <DialogContent>
              <OrgAdminProjectSettingsUserTable
                projectId={selectedProject.id}
                usersPartialUserProject={projectUsers}
                isUpdating={isUpdating}
                showSelect={mode === 'invite-members'}
                allEditableRole
                showRoleErrors
                isInvite={mode === 'invite-members'}
                onSelectionChange={(selectedUsers: AnyUser[]): void => {
                  setSelectedUsers(selectedUsers);
                }}
                onPermissionChange={onPermissionChange}
                onPermissionChangeCommit={_updateMembers}
                onUserRemovalStart={onUserRemovalStart}
                onTransferOwnershipStart={onTransferOwnershipStart}
                tableRef={tableRef}
              />
            </DialogContent>
          </>
        )}
        <div style={{ height: '8px' }} />
      </Dialog>
      <AddMembersToPlatformDialog
        open={open && openInvite}
        projectId={selectedProject?.id}
        onClose={(): void => setOpenInvite(false)}
        inviteUser={(email, userRole, projectId?, projectRole?) => {
          inviteUser(email, userRole, projectId, projectRole);
          onCancel();
        }}
      />
    </>
  );
};

export default ManageProjectAccessDialog;
